import React, { useState, useEffect } from 'react';
import './GuideModal.css';

const GuideModal = ({ isOpen, onClose, onVersionUpdate }) => {
  const [activeSection, setActiveSection] = useState('what-is-pixlsizer');
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchGuideContent();
    }
  }, [isOpen]);

  const convertMarkdownToHTML = (markdown) => {
    let html = markdown;
    
    // Escape HTML
    html = html.replace(/&/g, '&amp;');
    html = html.replace(/</g, '&lt;');
    html = html.replace(/>/g, '&gt;');
    
    // Convert headers
    html = html.replace(/^### (.*?)$/gm, '<h3>$1</h3>');
    html = html.replace(/^## (.*?)$/gm, '<h2>$1</h2>');
    html = html.replace(/^# (.*?)$/gm, '<h1>$1</h1>');
    
    // Convert bold and italic
    html = html.replace(/\*\*\*([^*]+)\*\*\*/g, '<strong><em>$1</em></strong>');
    html = html.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
    html = html.replace(/\*([^*]+)\*/g, '<em>$1</em>');
    
    // Convert links
    html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>');
    
    // Convert email addresses to mailto links
    html = html.replace(/([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/g, '<a href="mailto:$1">$1</a>');
    
    // Convert bullet points
    html = html.replace(/^- (.+)$/gm, '<li>$1</li>');
    // Wrap consecutive <li> elements in <ul>
    html = html.replace(/(<li>.*<\/li>\s*)+/g, function(match) {
      return '<ul>' + match + '</ul>';
    });
    
    // Convert line breaks and paragraphs
    const lines = html.split('\n');
    const processedLines = [];
    let inList = false;
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      if (line === '') {
        if (!inList) {
          processedLines.push('</p><p>');
        }
      } else if (line.startsWith('<ul>')) {
        inList = true;
        processedLines.push(line);
      } else if (line.includes('</ul>')) {
        inList = false;
        processedLines.push(line);
      } else {
        processedLines.push(line);
      }
    }
    
    html = '<p>' + processedLines.join('\n') + '</p>';
    
    // Clean up
    html = html.replace(/<p><\/p>/g, '');
    html = html.replace(/<p>(<h[1-3]>)/g, '$1');
    html = html.replace(/(<\/h[1-3]>)<\/p>/g, '$1');
    html = html.replace(/<p>(<ul>)/g, '$1');
    html = html.replace(/(<\/ul>)<\/p>/g, '$1');
    html = html.replace(/\n/g, '<br>');
    html = html.replace(/<br><h/g, '<h');
    html = html.replace(/<br><ul>/g, '<ul>');
    html = html.replace(/<\/ul><br>/g, '</ul>');
    
    // Handle Q&A format
    html = html.replace(/<p><strong>Q: (.*?)<\/strong><br>/g, '<div class="guide-question">Q: $1</div>');
    html = html.replace(/A: (.*?)(<\/p>|<br><strong>Q:|$)/g, '<div class="guide-answer">A: $1</div>$2');
    
    return html;
  };

  const fetchGuideContent = async () => {
    setLoading(true);
    
    try {
      // Get last seen version from localStorage
      const lastSeenVersion = localStorage.getItem('pixlsizer-guide-version');
      
      const response = await fetch(`https://www.pixlsizer.com/api/guide-content${lastSeenVersion ? `?lastSeenVersion=${lastSeenVersion}` : ''}`);
      const data = await response.json();
      
      if (data.sections && Array.isArray(data.sections)) {
        // Process sections to convert markdown to HTML
        const processedSections = data.sections.map(section => ({
          ...section,
          htmlContent: convertMarkdownToHTML(section.content)
        }));
        setSections(processedSections);
        
        // Update version in localStorage
        if (data.version) {
          localStorage.setItem('pixlsizer-guide-version', data.version);
          
          // Clear notification if viewing new version
          if (onVersionUpdate) {
            onVersionUpdate(false);
          }
        }
      } else {
        // Fallback to default sections
        const defaultSections = getDefaultSections();
        const processedSections = defaultSections.map(section => ({
          ...section,
          htmlContent: convertMarkdownToHTML(section.content)
        }));
        setSections(processedSections);
      }
    } catch (err) {
      console.error('Error fetching guide content:', err);
      // Don't show error to users, just silently use default content
      const defaultSections = getDefaultSections();
      const processedSections = defaultSections.map(section => ({
        ...section,
        htmlContent: convertMarkdownToHTML(section.content)
      }));
      setSections(processedSections);
    } finally {
      setLoading(false);
    }
  };

  const getDefaultSections = () => [
    {
      id: 'what-is-pixlsizer',
      title: 'What is PixlSizer?',
      content: `PixlSizer is a professional batch image processing tool designed for photographers, content creators, and businesses who need to efficiently resize and optimize multiple images at once. Built with modern technology and a focus on speed, PixlSizer combines traditional image processing with cutting-edge AI capabilities to deliver professional results in seconds.`
    },
    {
      id: 'app-licensing',
      title: 'App Licensing',
      content: `- **License Activation**: Enter your license key and email on first launch to unlock features
- **License validation requires a one-time internet connection**
- **License status is cached locally for offline use**
- **No personal data beyond email is stored for licensing**
- **License Transfer**: Your license can be transferred by deactivating it on your primary system (e.g. Desktop) and reactivating it on the secondary system (e.g. Laptop) unlimited times`
    },
    {
      id: 'core-features',
      title: 'Core App Features',
      content: `### Batch Processing (Offline)
- **Resize Multiple Images**: Process hundreds of images simultaneously
- **Aspect Ratio Preservation**: Maintains image proportions automatically
- **Multiple Format Support**: Export to AVIF, WebP, JPEG, or PNG
- **Smart Quality Settings**: Optimized defaults for each format
- **Custom Naming**: Add suffixes to prevent overwriting originals

### AI-Powered Features (Online - Pro/Agency/Partner Only)
- **AI Upscaling**: Automatically enhance low-resolution images
- **AI Crop Fill**: Intelligently fill empty areas when cropping to exact dimensions
- **Multiple Iterations**: Generate up to 9 variations with AI Fill

### Cloud Features (Pro/Agency/Partner Only)
- **Direct S3 Upload**: Send processed images directly to cloud storage
- **CSV Export**: Cloud reports including paths can be uploaded to any website CMS for quick gallery creation`
    },
    {
      id: 'privacy-security',
      title: 'Privacy and Security',
      content: `### Your Data, Your Control
- **Local-First Design**: Core features work entirely offline
- **No Account Required**: Use the app without creating an account
- **No Analytics**: We don't track your usage or collect personal data
- **Secure Transmission**: All AI processing uses encrypted HTTPS connections

### AI Processing Transparency
When using AI features, please be aware:
- Images are processed via Cloud, the same way DALL-E, Stable Diffusion, Flux, Midjourney, Google, Chat GPT etc also process images
- Images are used solely for processing and are not used for AI training
- Temporary storage is required for technical processing (auto-deleted within 2-24 hours)`
    },
    {
      id: 'server-ai-advantages',
      title: 'Advantages of Server-based AI',
      content: `### Performance
By leveraging cloud AI, we can process images 10-100x faster than consumer hardware would allow. A 4x upscale that might take 5-10 minutes on a high-end gaming PC completes in seconds using enterprise GPU clusters.

### Accessibility
Not everyone has access to $5,000+ GPU hardware. Cloud AI makes professional image enhancement accessible to everyone at a fraction of the cost.

### Flexibility
The hybrid approach gives you the best of both worlds:
- Fast, private local processing for everyday tasks
- Powerful AI enhancement when you need it
- No internet required for basic features`
    },
    {
      id: 'best-practices',
      title: 'Best Practices',
      content: `### For Maximum Privacy
- Use local features (resize, crop, convert) for sensitive images
- These features work 100% offline with no data transmission

### For Best Results
- Use AI Upscale for low-resolution images that need enhancement
- Use AI Fill for social media crops that need background extension
- Process in batches to maximize efficiency

### For Large Projects
- Sort and organize images before processing
- Use consistent naming conventions
- Take advantage of cloud upload for team collaboration`
    },
    {
      id: 'faq',
      title: 'Frequently Asked Questions',
      content: `**Q: Can I use PixlSizer completely offline?**
A: Yes! All core features (resize, crop, format conversion) work offline. Only AI features and cloud upload require internet.

**Q: Are my images safe when using AI features?**
A: Yes. Images are transmitted securely, processed immediately, and automatically deleted from processing servers. We follow industry-standard security practices.

**Q: What happens to my images after AI processing?**
A: Processed images are saved to your local device. Temporary processing files on Replicate's servers are automatically deleted, typically within 24 hours.

**Q: Do you use my images to train AI?**
A: No. Your images are used solely for the processing you request and are never used for AI training or any other purpose.

**Q: Can I have more than one license?**
A: Yes, you may purchase a second license from pixlsizer.com at any time if you do not wish to deactivate and reactivate licenses across multiple devices.

**Q: Do you offer a private version of the software for AI processing?**
A: We are currently reviewing requests from users who would prefer to process content offline and working on a version that will allow you to download models locally. However, this will require significant GPU processing power and only work on high-performance systems with dedicated graphics cards. For more information about our private app, please contact support@pixlevents.com to get on the waiting list, and we will inform you as soon as it becomes available.`
    },
    {
      id: 'contact-support',
      title: 'Contact and Support',
      content: `For questions, concerns, or feedback about PixlSizer:
- **Website**: [pixlsizer.com](https://pixlsizer.com)
- **Email**: support@pixlevents.com
- **Documentation**: Available within the app

---

*PixlSizer is developed by PixlEvents, committed to empowering creators with professional tools that respect privacy and deliver results.*`
    }
  ];

  const handleSectionClick = (sectionId) => {
    setActiveSection(activeSection === sectionId ? null : sectionId);
  };

  if (!isOpen) return null;

  return (
    <div className="guide-modal-overlay" onClick={onClose}>
      <div className="guide-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="guide-modal-header">
          <h2>PixlSizer User Guide</h2>
          <button className="guide-modal-close" onClick={onClose}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>
        <div className="guide-modal-body">
          {loading && <div className="guide-loading">Loading guide...</div>}
          {!loading && sections.length > 0 && (
            <div className="guide-accordion">
            {sections.map((section) => (
              <div key={section.id} className="guide-accordion-item">
                <button
                  className={`guide-accordion-header ${activeSection === section.id ? 'active' : ''}`}
                  onClick={() => handleSectionClick(section.id)}
                >
                  <span>{section.title}</span>
                  <svg
                    className="guide-accordion-arrow"
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <polyline points="4 6 8 10 12 6"></polyline>
                  </svg>
                </button>
                <div
                  className={`guide-accordion-content ${activeSection === section.id ? 'active' : ''}`}
                  dangerouslySetInnerHTML={{ __html: section.htmlContent || section.content }}
                />
              </div>
            ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GuideModal;